import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PromiseState } from 'react-refetch';
import { stringify } from 'querystring';
import interceptConnect from 'helpers/interceptConnect';
import moment from 'moment';

import { EntityContext } from 'context/entityContext';
import useComponentSettings from 'hooks/useComponentSettings';
import useHasNoData from 'components/data-containers/PerformanceTrend/hooks/useHasNoData';

import useMedia from 'hooks/useMedia';
import usePerformanceTrendData from './hooks/usePerformanceTrendData';
import useExportUrl from '../KPIs/Discovery/utils/useExportUrl';

import getSchema from 'schemas/componentSettings/shortFormVideoTrend';
import formatDate from 'helpers/formatDate';
import getShortFormVideoColors from 'helpers/getShortFormVideoColors';
import getColorScale from './helpers/getColorScale';
import getEntityKey from 'helpers/getEntityKey';
import getMargins from 'components/data-visualization/LineChart/helpers/getMargins';
import existsIn from 'components/data-containers/PerformanceTrend/helpers/existsIn';
import getTrendPoints from 'components/data-containers/PerformanceTrend/helpers/getTrendPoints';
import getDataTogglePairs from './helpers/getDataTogglePairs';
import { getInitialPeriod } from '../PerformanceTrend/helpers/getInitialPeriod';

import colors from 'styles/themes/colors';
import { ILabelValuePair } from 'types/shared';
import { MAX_LIMIT } from 'config';

import Box from 'components/elements/Box';
import GenericComponent from 'components/utility/GenericComponent/GenericComponent';
import Dropdown from 'components/base/Dropdown/Dropdown';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import KeyValueLoader from 'components/utility/ContentLoader/KeyValueLoader/KeyValueLoader';
import PeriodDropdown from 'components/utility/PeriodDropdown';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import LineChart from './components/LineChart';
import Chart from 'components/data-visualization/Chart/Chart';
import LineChartSkeleton from 'components/data-visualization/LineChart/LineChartSkeleton/LineChartSkeleton';
import RadioGroup from 'components/base/RadioGroup/RadioGroup';
import ComponentControls from 'components/styled/ComponentControls/ComponentControls';
import DataToggleList from 'components/DataToggleList/DataToggleList';
import Legend from 'components/data-containers/PerformanceTrend/shared/Legend';
import DateRange from 'components/base/DateRange/DateRange';
import Switcher from 'components/Switcher';

import { PeriodKey } from 'helpers/formatDate';
import periodTypes, { customDateType } from 'maps/period-types';
import {
  tiktokIds,
  allPlatforms,
} from 'components/utility/FilterDisplayName/FilterDisplayName';

import {
  VisiblePeriod,
  VisiblePeriodWrapper,
  Relative,
  StyledPerformanceTrendLoader,
  ShortFormRadioWrapper,
} from '../PerformanceTrend/shared/PerformanceTrend.style';
import {
  ChartWrapper,
  ControlsContainer,
  GenericComponentWrapper,
  PeriodDropdownWrapper,
  VisiblePeriodBox,
} from './ShortFormVideoTrend.styles';
import { StyledNoData } from 'components/data-visualization/styles';

interface IPerformanceTrendProps {
  actualityData: PromiseState<any>;
  title: string;
  componentId: string;
  shortFormVideoTrendFetch: (props: any) => any;
  shortFormVideoTrendData: PromiseState<any>;
  shortFromVideoActualityFetch: any;
}

interface IOuterProps {
  componentId: string;
  title: string;
}

type TContentType = 'total' | 'ugc' | 'pgc' | 'ugc-pgc';

interface IComponentSettings {
  selectedInterval: string;
  breakdown: string;
  selectedCustomer: string;
  lastSaved: string;
  contentType: TContentType;
  selectedMetricType: 'views' | 'creations';
  platformType: 'all platforms' | 'by platform';
  customDateRange: {
    fromDate: number;
    toDate: number;
  };
}

const metricContentTypes = [
  'ugc-views',
  'pgc-views',
  'ugc-creations',
  'pgc-creations',
];

const tooltipText =
  'Right now, we only receive\n monthly data for YouTube Shorts';

const dataSources = (): any => {
  return {
    shortFormVideoTrendFetch: (props: {
      countryIds: any;
      region: string;
      platform: number;
      shortFormVideoEntityType: any;
      entityIds: string[];
      limit: number;
      period: any;
      compareMode: boolean;
      selectedMetricType: 'views' | 'creations';
      selectedContentType: TContentType;
      customRegion: any;
      customDate?: { fromDate: number; toDate: number } | null;
      selectedInterval: string;
    }): any => {
      const params =
        stringify(props as any) +
        `${props.selectedInterval}_${props.customDate?.fromDate}_${props.customDate?.toDate}`;

      return {
        shortFormVideoTrendData: {
          url: `shortFormVideotrend?${params}`,
          interceptionData: props,
        },
      };
    },
    shortFromVideoActualityFetch: (props: any): any => {
      return {
        actualityData: {
          url: `shortFormVideoActuality?${stringify(props as any)}`,
          interceptionData: props,
        },
      };
    },
  };
};

const ShortFormVideoTrend: React.FC<IPerformanceTrendProps> = (props) => {
  const {
    actualityData = PromiseState.create(),
    title,
    componentId,
    shortFormVideoTrendFetch,
    shortFormVideoTrendData = PromiseState.create(),
    shortFromVideoActualityFetch,
  } = props;

  const { fulfilled, pending, rejected, reason, value } =
    shortFormVideoTrendData;
  const errorCause = value?.reason;
  const {
    shortFormVideoPGCViewsData: trendViewsData,
    shortFormVideoPGCCreationsData: trendCreationsData,
    shortFormVideoUGCViewsData,
    shortFormVideoUGCCreationsData,
    pgcViewsUrl,
    pgcCreationsUrl,
    ugcViewsUrl,
    ugcCreationsUrl,
  } = !!value && value;

  const exportUrlViewsUrl = useExportUrl(pgcViewsUrl);
  const exportUrlCreations = useExportUrl(pgcCreationsUrl);
  const exportUrlUGCViews = useExportUrl(ugcViewsUrl);
  const exportUrlUGCCreations = useExportUrl(ugcCreationsUrl);

  const context = useContext(EntityContext);
  const {
    entityIds,
    entityType,
    countryIds,
    filters,
    dataColors,
    compareMode,
    customRegionFilter,
  } = context;

  const isDisabledUGCPGC = !!(
    filters?.platform && !tiktokIds.includes(filters?.platform)
  );
  const isYTShorts = filters?.platform === 291943;
  const isChina = filters?.country === 'CN';

  const radioPlatformType = [
    {
      label: 'All Platforms',
      value: 'all platforms',
    },
    {
      label: 'By Platform',
      value: 'by platform',
      disabled: !!filters?.platform,
    },
  ];

  const tooltipTextCompare = isDisabledUGCPGC
    ? 'UGC/PGC do not available for this platform'
    : undefined;
  const shortFormVideoCompareTypes: ILabelValuePair<string>[] = [
    {
      label: 'Total Views',
      value: 'total-views',
      metricType: 'views',
      contentType: 'total',
    },
    {
      label: 'Total Creations',
      value: 'total-creations',
      metricType: 'creations',
      contentType: 'total',
    },
    {
      label: 'UGC Views',
      value: 'ugc-views',
      metricType: 'views',
      contentType: 'ugc',
      tooltipContent: tooltipTextCompare,
      disabled: isDisabledUGCPGC,
    },
    {
      label: 'UGC Creations',
      value: 'ugc-creations',
      metricType: 'creations',
      contentType: 'ugc',
      tooltipContent: tooltipTextCompare,
      disabled: isDisabledUGCPGC,
    },
    {
      label: 'PGC Views',
      value: 'pgc-views',
      metricType: 'views',
      contentType: 'pgc',
      tooltipContent: tooltipTextCompare,
      disabled: isDisabledUGCPGC,
    },
    {
      label: 'PGC Creations',
      value: 'pgc-creations',
      metricType: 'creations',
      contentType: 'pgc',
      tooltipContent: tooltipTextCompare,
      disabled: isDisabledUGCPGC,
    },
  ];

  useEffect(() => {
    if (isYTShorts)
      saveComponentSettings({
        selectedInterval: 'month',
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isYTShorts]);

  const isSmallScreen = useMedia('(max-width: 48em)');
  const margin = getMargins(true);

  const [tempPeriod, setTempPeriod] = useState<Date | null>(null);
  const [period, setPeriod] = useState<any>();
  const [selectedTrendPoints, setSelectedTrendPoints] = useState([]);
  const [tempSelectedTrendPoints, setTempSelectedTrendPoints] = useState();
  const [disabledTrends, setDisabledTrends] = useState<any[]>([]);
  const [selectedShortFormVideoType, selectShortFormVideoType] = useState(
    compareMode ? shortFormVideoCompareTypes[0] : null
  );
  const [isViewsCreationsView, setViewsCreationsView] = useState(true);
  const itemToString = (item: any): string => item.value;

  const shortFormVideoTrendSchema = getSchema();
  const settingsId = componentId;
  const [componentSettings, saveComponentSettings, , , isLoadedSettings] =
    useComponentSettings(settingsId, shortFormVideoTrendSchema);

  const allPlarformsIds: number[] = useMemo(() => {
    if (!isViewsCreationsView) {
      return [isChina ? 285185 : 285184];
    }
    return allPlatforms.reduce((filtered: number[], platform) => {
      // if Chine -> Douyin. if not -> TikTok
      if (
        (!isChina && platform.id !== 285185) ||
        (isChina && platform.id !== 285184)
      ) {
        filtered.push(platform.id);
      }
      return filtered;
    }, []);
  }, [isChina, isViewsCreationsView]);

  useEffect(() => {
    if (isDisabledUGCPGC) {
      saveComponentSettings({
        contentType: 'total',
        selectedMetricType: compareMode ? 'views' : 'total',
      });
      selectShortFormVideoType(shortFormVideoCompareTypes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisabledUGCPGC, compareMode]);

  useEffect(() => {
    if ((filters?.platform && platformType === 'by platform') || compareMode) {
      saveComponentSettings({
        platformType: 'all platforms',
      });
    }
  }, [filters?.platform, compareMode]);

  const {
    selectedInterval,
    contentType,
    platformType,
    selectedMetricType,
    customDateRange,
  } = componentSettings as IComponentSettings;
  const extendedPeriodTypes = useMemo(() => {
    if (periodTypes) {
      Object.keys(periodTypes).forEach((key) => {
        periodTypes[key] = {
          ...periodTypes[key],
          disabled: isYTShorts ? key === 'day' || key === 'week' : false,
          tooltipContent:
            isYTShorts && (key === 'day' || key === 'week')
              ? tooltipText
              : undefined,
        };
      });
      const customDate = {
        customDate: {
          ...customDateType['customDate'],
          disabled: isYTShorts,
          tooltipContent: isYTShorts ? tooltipText : undefined,
        },
      };
      return Object.assign({}, periodTypes, customDate);
    }
    return Object.assign({}, periodTypes, customDateType);
  }, [isYTShorts]);

  const interval = extendedPeriodTypes[selectedInterval];
  const totalContentType = contentType === 'total';

  const compareIds: any = useMemo(
    () =>
      compareMode
        ? compareMode === 'entity' && entityIds && entityIds.length > 0
          ? entityIds
          : countryIds
        : [],
    [compareMode, entityIds, countryIds]
  );

  const getMetricType = useCallback(
    (): string[] =>
      compareIds.map((id: string) => {
        switch (compareMode) {
          case 'entity':
            return contentType !== 'total'
              ? `${id}-${contentType}-${selectedMetricType}`
              : `${id}-${selectedMetricType}`;
          case 'country':
            return contentType !== 'total'
              ? `${entityIds[0]}-${contentType}-${selectedMetricType}-${id}`
              : `${entityIds[0]}-${selectedMetricType}-${id}`;
          default:
            return '';
        }
      }),
    [selectedMetricType, entityIds, contentType, compareIds, compareMode]
  );

  const [showDateRange, setShowDateRange] = useState(false);
  const [customDateValue, setCustomDateValue] = useState<{
    fromDate: number;
    toDate: number;
  } | null>(null);

  useEffect(() => {
    const isCustomDateSet =
      selectedInterval !== 'customDate' ||
      (selectedInterval === 'customDate' && customDateValue?.toDate);
    if (isLoadedSettings && isCustomDateSet) {
      const requestParameters = {
        countryIds: compareMode === 'country' ? countryIds : filters?.country,
        region: filters?.region,
        platform: filters?.platform,
        sfvEntityType: entityType,
        entityIds,
        limit: MAX_LIMIT,
        period: selectedInterval === 'customDate' ? 'week' : selectedInterval,
        totalContentType: isDisabledUGCPGC ? true : totalContentType,
        compareMode,
        selectedMetricType,
        selectedContentType: isDisabledUGCPGC ? 'total' : contentType,
        customRegion: customRegionFilter,
        customDate: selectedInterval === 'customDate' ? customDateValue : null,
        selectedInterval,
        breakdown: platformType === 'by platform' ? allPlarformsIds : undefined,
      };
      shortFormVideoTrendFetch(requestParameters);
    }
  }, [
    isLoadedSettings,
    countryIds,
    entityType,
    entityIds,
    interval.value,
    filters,
    shortFormVideoTrendFetch,
    totalContentType,
    compareMode,
    selectedMetricType,
    contentType,
    customRegionFilter,
    customDateValue,
    selectedInterval,
    isDisabledUGCPGC,
  ]);

  const trends = useMemo(() => {
    return [
      shortFormVideoUGCViewsData,
      trendViewsData,
      shortFormVideoUGCCreationsData,
      trendCreationsData,
    ];
  }, [
    trendViewsData,
    trendCreationsData,
    shortFormVideoUGCViewsData,
    shortFormVideoUGCCreationsData,
  ]);

  useEffect(() => {
    const trendWithData = trends.find((trend: any) => !!trend?.length);
    if (!trendWithData) return;
    const initialPeriod = getInitialPeriod(trendWithData);
    setPeriod(initialPeriod);
  }, [trends]);

  const sortedTrends = usePerformanceTrendData(
    trends,
    setPeriod,
    compareMode,
    compareIds
  );

  /**
   * Memoize the filtered trends
   * @type {Array}
   */
  const filteredTrends = useMemo(() => {
    if (disabledTrends.length !== 0) {
      return sortedTrends
        .filter((trend: any) => !disabledTrends.includes(getEntityKey(trend)))
        .filter((trend: any) => trend !== undefined);
    }

    return sortedTrends;
  }, [disabledTrends, sortedTrends]);

  /**
   * Set the currently selected (by clicking) data points
   * @type {Array}
   */
  useEffect(() => {
    if (
      !period ||
      !filteredTrends.length ||
      filteredTrends?.some((trend: any) => trend === undefined)
    )
      return;
    setSelectedTrendPoints(getTrendPoints(filteredTrends, period));
  }, [filteredTrends, period]);

  /**
   * Set the currently temporarily selected (by hovering) data points
   * @type {Array}
   */
  useEffect(() => {
    setTempSelectedTrendPoints(getTrendPoints(filteredTrends, tempPeriod));
  }, [filteredTrends, tempPeriod]);

  /**
   * If trend data updates, check if all disabled trends are
   * still valid. Remove the invalid ones.
   */
  useEffect(() => {
    if (!sortedTrends) return;
    const keys = sortedTrends.map((t: any) => getEntityKey(t));

    // Are all disabled trends valid?
    const hasOnlyValidKeys = disabledTrends.every((key) => keys.includes(key));
    if (hasOnlyValidKeys) return;

    // Don't allow disabled for 2 or less trends.
    if (disabledTrends.filter((key) => keys.includes(key)).length <= 2) {
      return setDisabledTrends([]);
    }

    setDisabledTrends(disabledTrends.filter((key) => keys.includes(key)));
  }, [disabledTrends, sortedTrends]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    saveComponentSettings({
      contentType: 'total',
      selectedMetricType: compareMode ? 'views' : 'both',
    });
  }, [compareMode]);

  /**
   * Boolean to indicate no data.
   */
  const hasNoData = useHasNoData(fulfilled, sortedTrends);

  /**
   * Update the time period displayed when you hover in the graph. Only displayed
   * for devices without touch capabilities.
   *
   * @param  {Date} nextPeriod timestamp in ms
   */
  function onTempPeriodChange(nextPeriod: Date): void {
    if (existsIn(sortedTrends, nextPeriod)) {
      setTempPeriod(nextPeriod);
    }

    // When a user moves the mouse out of the linechart hitbox, this is called with null.
    if (nextPeriod === null) {
      setTempPeriod(nextPeriod);
    }
  }
  /**
   * Update the time period displayed when you move the mouse out of the trend graph
   * Set this time period by clicking or tapping in the graph.
   *
   * @param  {Date} nextPeriod timestamp in ms
   */
  function onPeriodChange(nextPeriod: Date): void {
    if (existsIn(sortedTrends, nextPeriod)) {
      setPeriod(nextPeriod);
    }
  }

  /**
   * Event handler for period type updates
   *
   * @param  {Object} interval Contains a label/value for period types
   */
  function onPeriodTypeChange(interval: {
    value: string;
    label: string;
  }): void {
    if (interval.value === 'customDate') {
      setShowDateRange(true);
    }
    saveComponentSettings({
      selectedInterval: interval.value,
    });
  }

  let shortFormVideoColors = [
    colors.PeachOrange,
    colors.VioletPurple,
    colors.PaleGreen,
    colors.VibrantOrange,
  ];
  if (totalContentType) {
    shortFormVideoColors = getShortFormVideoColors(filters?.platform);
  }
  if (
    platformType === 'by platform' &&
    sortedTrends?.length > 0 &&
    isViewsCreationsView
  ) {
    shortFormVideoColors = [].concat(
      ...sortedTrends
        ?.map(
          (trend: any, index: number) =>
            index % 2 === 0 &&
            getShortFormVideoColors(trend?.breakdownCustomerId)
        )
        .filter((trend: any) => trend)
    );
  }

  const metricTypes =
    platformType === 'by platform' && sortedTrends?.length
      ? sortedTrends?.map((trend: any) => trend?.breakdown)
      : ['views', 'creations'];

  const colorScale = useCallback(
    getColorScale(
      compareMode ? dataColors : shortFormVideoColors,
      compareMode
        ? getMetricType()
        : totalContentType
        ? metricTypes
        : metricContentTypes
    ),
    [sortedTrends]
  );

  /**
   * Create the label/value pairs for DataToggleList
   */
  const dataTogglePairs = useMemo(
    () => getDataTogglePairs(sortedTrends, tempPeriod || period, colorScale),
    [colorScale, period, tempPeriod, sortedTrends]
  );
  const handleTrendToggle = (id: any): void => {
    if (disabledTrends.includes(id)) {
      return setDisabledTrends(disabledTrends.filter((d) => d !== id));
    }

    setDisabledTrends([...disabledTrends, id]);
  };

  /**
   * Handle updating the selected content type, either 'total' or 'ugc-pgc' or 'ugc' or 'pgc'
   * and metric type 'creations' or 'views'.
   */
  const handleContentTypeChange = (item: any): void => {
    saveComponentSettings({
      contentType: compareMode ? item?.contentType : item.value,
      selectedMetricType: compareMode ? item?.metricType : item.value,
    });
  };

  const handlePlatformTypeChange = (item: any): void => {
    saveComponentSettings({
      platformType: item.value,
    });
  };

  const setCheck = (param: boolean): void => {
    if (!isDisabledUGCPGC) {
      saveComponentSettings({
        contentType: param ? 'total' : 'ugc-pgc',
        selectedMetricType: param ? 'total' : 'ugc-pgc',
      });
      setViewsCreationsView(param);
    }
  };

  /**
   * Event handler for date range value updates
   *
   * @param  {Object} interval Contains a label/value for period types
   */
  function onDateRangeChange(fromDate: any, toDate: any): void {
    setCustomDateValue({ fromDate, toDate });

    saveComponentSettings({
      customDateRange: {
        fromDate,
        toDate,
      },
    });
  }

  useEffect(() => {
    if (selectedInterval === 'customDate') {
      shortFromVideoActualityFetch({
        /** For custom dates we alsways have weekly type */
        customerFilter: filters?.platform,
        expand: 'weekly',
        limit: MAX_LIMIT,
      });
    }
  }, [filters?.platform, selectedInterval]);

  const minMaxDateRangeData = useMemo(() => {
    let minDate = moment(actualityData?.value?.actualityData).subtract(
      52,
      'week'
    );
    minDate = minDate.day(minDate.day() <= 5 ? 6 : -1);
    return {
      minDate: moment(minDate),
      maxDate: moment(actualityData?.value?.actualityData),
    };
  }, [actualityData, selectedInterval]);

  useEffect(() => {
    if (customDateRange) {
      if (
        moment(customDateRange.fromDate).isBefore(
          minMaxDateRangeData.minDate,
          'day'
        ) ||
        moment(customDateRange.toDate).isAfter(
          minMaxDateRangeData.maxDate,
          'day'
        )
      ) {
        const range = {
          fromDate: moment(minMaxDateRangeData.maxDate)
            .subtract(1, 'months')
            .valueOf(),
          toDate: moment(minMaxDateRangeData.maxDate).valueOf(),
        };
        saveComponentSettings({
          customDateRange: range,
        });
        setCustomDateValue(range);
      } else {
        setCustomDateValue(customDateRange);
      }
    }
  }, [customDateRange, minMaxDateRangeData, saveComponentSettings]);

  const additionalLabel = useMemo(() => {
    if (customDateValue && selectedInterval === 'customDate') {
      return `${moment(customDateValue.fromDate).format(
        'MMM DD, YYYY'
      )} - ${moment(customDateValue.toDate).format('MMM DD, YYYY')}`;
    }

    return null;
  }, [customDateValue, selectedInterval]);

  const extraToolbarItems = totalContentType
    ? [
        { label: 'Export views data', href: exportUrlViewsUrl },
        { label: 'Export creations data', href: exportUrlCreations },
      ]
    : [
        { label: 'Export UGC views data', href: exportUrlUGCViews },
        { label: 'Export PGC views data', href: exportUrlViewsUrl },
        { label: 'Export UGC creations data', href: exportUrlUGCCreations },
        { label: 'Export PGC creations data', href: exportUrlCreations },
      ];

  return (
    <GenericComponentWrapper>
      <GenericComponent
        title={title}
        glossarySlug="shortFormVideo/performance"
        allowedFilters={['country', 'region', 'customRegion', 'platform']}
        loading={pending}
        success={fulfilled}
        error={rejected}
        extraToolbarItems={extraToolbarItems}
        isNarrowScreen
      >
        <ErrorBoundary
          error={
            reason || errorCause?.error
              ? new Error(`Error reason: ${reason || errorCause?.error}`)
              : null
          }
          errorInfo={
            reason || errorCause?.errorInfo
              ? reason?.cause || errorCause?.errorInfo
              : undefined
          }
        >
          <ControlsContainer>
            <Box pt={4}>
              <ComponentControls>
                <VisiblePeriodBox>
                  <VisiblePeriodWrapper>
                    {period && (
                      <VisiblePeriod
                        label={
                          selectedInterval === 'customDate'
                            ? 'week'
                            : selectedInterval
                        }
                        value={formatDate(
                          tempPeriod || period,
                          (selectedInterval === 'customDate'
                            ? 'week'
                            : selectedInterval) as PeriodKey
                        )}
                        dataColor={colors.DeepBlue}
                      />
                    )}
                    {!period && <KeyValueLoader />}
                  </VisiblePeriodWrapper>
                </VisiblePeriodBox>

                {!compareMode && (
                  <ShortFormRadioWrapper>
                    <Switcher
                      isChecked={isViewsCreationsView}
                      leftLabel="Views/Creations"
                      rightLabel="UGC/PGC"
                      disabled={isDisabledUGCPGC}
                      setCheck={setCheck}
                      isWithoutMargins
                    />
                    <RadioGroup
                      options={radioPlatformType}
                      name="shortFormVideo-platformType-options"
                      selectedOption={platformType}
                      onChange={handlePlatformTypeChange}
                      tooltipMaxWidth="220px"
                      isButtonNewDesigned
                    />
                  </ShortFormRadioWrapper>
                )}

                {compareMode && (
                  <Dropdown
                    onChange={(item: any): void => {
                      selectShortFormVideoType(item);
                      handleContentTypeChange(item);
                    }}
                    itemToString={itemToString}
                    items={shortFormVideoCompareTypes}
                    selectedItem={selectedShortFormVideoType}
                    isRoundedDropdown="all"
                  />
                )}

                {isSmallScreen && (
                  <PeriodDropdownWrapper>
                    <PeriodDropdown
                      selectedItem={interval}
                      onChange={onPeriodTypeChange}
                      isRoundedDropdown="all"
                    />
                  </PeriodDropdownWrapper>
                )}

                {!isSmallScreen && (
                  <PeriodDropdown
                    selectedItem={interval}
                    onChange={onPeriodTypeChange}
                    customPeriods={extendedPeriodTypes}
                    onSelect={(el: { label: string; value: string }): void => {
                      if (el.value === 'customDate') {
                        setShowDateRange(true);
                      }
                    }}
                    isRoundedDropdown="all"
                  />
                )}
                {showDateRange && (
                  <DateRange
                    startDate={
                      customDateValue && customDateValue.fromDate
                        ? new Date(customDateValue.fromDate)
                        : undefined
                    }
                    endDate={
                      customDateValue && customDateValue.toDate
                        ? new Date(customDateValue.toDate)
                        : undefined
                    }
                    minDate={minMaxDateRangeData.minDate}
                    maxDate={minMaxDateRangeData.maxDate}
                    onCloseCalendar={(): void => setShowDateRange(false)}
                    onChange={onDateRangeChange}
                    margin={isSmallScreen ? { top: 103 } : null}
                  />
                )}
              </ComponentControls>
            </Box>

            <DataToggleList
              data={dataTogglePairs}
              loading={pending}
              handleClick={handleTrendToggle}
              toggledItems={disabledTrends}
              expectedItemCount={dataTogglePairs?.length}
              withTooltip
              isSmallScreen={isSmallScreen}
              isFullValueDisplayed={isSmallScreen}
            />
          </ControlsContainer>

          <ChartWrapper>
            <AnimatedLoadingWrapper loading={pending}>
              <Relative>
                <Chart
                  showNoDataWarning={
                    (fulfilled && !filteredTrends) || hasNoData
                  }
                  height={isSmallScreen ? 260 : 420}
                  hasNoData={fulfilled && hasNoData}
                  mediaQuery="(min-width: 40em)"
                  isDimmed={pending}
                  render={(renderProps): JSX.Element => (
                    <>
                      {fulfilled &&
                        filteredTrends &&
                        filteredTrends.length !== 0 &&
                        selectedTrendPoints.length &&
                        !filteredTrends?.some(
                          (trend: any) => trend === undefined
                        ) && (
                          <LineChart
                            fillFromLastNonNullValue
                            shouldTransitionIn={fulfilled}
                            period={interval.value}
                            colorScale={colorScale}
                            entities={filteredTrends}
                            selectedEntities={selectedTrendPoints}
                            temporarySelectedEntities={tempSelectedTrendPoints}
                            handleMouseHover={onTempPeriodChange}
                            handleMouseClick={onPeriodChange}
                            hasNoData={hasNoData}
                            label=""
                            additionalLabel={
                              selectedInterval === 'customDate'
                                ? additionalLabel
                                : null
                            }
                            isStrictDesign={true}
                            isSmallScreen={isSmallScreen}
                            {...renderProps}
                          />
                        )}
                      {(!selectedTrendPoints ||
                        !selectedTrendPoints.length) && (
                        <LineChartSkeleton
                          width={renderProps.parentWidth}
                          height={
                            renderProps.parentHeight -
                            margin.top -
                            margin.bottom
                          }
                          marginTop={margin.top}
                        />
                      )}
                    </>
                  )}
                />

                {pending && !selectedTrendPoints && (
                  <StyledPerformanceTrendLoader />
                )}

                {sortedTrends && disabledTrends.length === sortedTrends.length && (
                  <StyledNoData>
                    <h3>No metrics selected</h3>
                    <p>
                      This chart is empty because there are no metrics selected.
                    </p>
                  </StyledNoData>
                )}
              </Relative>
            </AnimatedLoadingWrapper>
          </ChartWrapper>
          <Legend />
        </ErrorBoundary>
      </GenericComponent>
    </GenericComponentWrapper>
  );
};

export default interceptConnect<IOuterProps>(dataSources as any)(
  ShortFormVideoTrend as any
);
